import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useFormik } from 'formik';

import clsx from 'clsx';

import DateField from '../FormFields/DateField';
import validationSchema from './BobTimestampFilter.schema';

import classes from './BobTimestampFilter.module.css';

const ExportDataTimestamp = ({
    className,
    dateFormat,
    desiredInterval,
    showTimeSelect,
    setStartTimestamp,
    setEndTimestamp,
    startTimestamp,
    endTimestamp,
    timeFormat,
    selectedDateDiff,
    setSelectedDateDiff
}) => {

    const interval = 5;
    const [maxRange, setMaxRange] = useState(525600);
    const [maxRangeMessage, setMaxRangeMessasge] = useState('1 Year');
    const maxMessage = <p>The maximum date range is <b>{maxRangeMessage}</b>. The start time must be at least 1 hour less than end time.</p>;

    useEffect(() => {
        const featureFlagEnabled = process.env.enableOneYearExport;

        switch (desiredInterval) {
            case 1:
                // 14 Days
                setMaxRange(20160);
                setMaxRangeMessasge('14 Days');
                break;
            case 5:
                // 31 Days
                setMaxRange(44640);
                setMaxRangeMessasge('31 Days');
                break;
            case 10:
                if (featureFlagEnabled) {
                    // 6 Months
                    setMaxRange(263520);
                    setMaxRangeMessasge('6 Months');
                    break;
                } else {
                    // 31 Days
                    setMaxRange(44640);
                    setMaxRangeMessasge('31 Days');
                    break;
                }
            default:
                if (featureFlagEnabled) {
                    // 1 Year
                    setMaxRange(525600);
                    setMaxRangeMessasge('1 Year');
                    break;
                } else {
                    // 31 Days
                    setMaxRange(44640);
                    setMaxRangeMessasge('31 Days');
                    break;
                }
        }
    }, [desiredInterval]);

    const { errors, touched, values, ...form } = useFormik({
        enableReinitialize: true,
        initialTouched:     {
            endTimestamp:   true,
            startTimestamp: true
        },
        initialValues: {
            endTimestamp:   endTimestamp,
            startTimestamp: startTimestamp
        },
        validateOnBlur: false,
        validationSchema
    });

    /**
     * Modifies the appropriate timestamp value based on the value of the
     * changed requisite date or time input.
     */
    function handleChange(name) {
        return (value) => {
            let parsedValue = null;
            if (value) {
                parsedValue = moment(value);
                // endtime should not be a future time. 
                if ( name === 'endTimestamp') {
                    const currentTimeStamp = moment().local();
                    if (parsedValue > currentTimeStamp) {
                        parsedValue = moment(currentTimeStamp);
                    }
                }
                form.setFieldValue(name, parsedValue, false);
            }
            form.setFieldTouched(name, true, false);
            onFilterChange(name, parsedValue, value);
            form.validateForm();
        };
    }

    const onFilterChange = async (filterName, value) => {
        if (filterName === 'startTimestamp') {
            if (value) {
                let start = value.local();
                if (moment(start).isSameOrAfter(moment(values.endTimestamp).local())) {
                    let end = moment(start).add(1, 'm').local();
                    if (moment(start).isSameOrAfter(moment().local())) {
                        end = moment().local();
                        start = moment(end).subtract(1, 'h');
                    }
                    form.setFieldValue('endTimestamp', end, false);
                    setEndTimestamp(end);
                    
                }
                if (moment(values.endTimestamp).local().diff(moment(start), 'm') >= maxRange) {
                    const end = moment(start).add(maxRange, 'm').local();
                    form.setFieldValue('endTimestamp', end, false);
                    setEndTimestamp(end);
                }
                else if (moment(values.endTimestamp).local().diff(start, 'minutes') < 60) {
                    if (moment(start).isAfter(moment().local()) || moment(values.endTimestamp).add(1, 'y').local().isAfter(moment().local())) {
                        start = moment().local().subtract(1, 'h');
                        const end = moment().local();
                        form.setFieldValue('endTimestamp', end, false);
                        setEndTimestamp(end);
                    }
                    else {
                        start = moment(values.endTimeStamp).local().subtract(1, 'h');
                    }
                }
                form.setFieldValue('startTimestamp', start, false);
                setStartTimestamp(start);
                setSelectedDateDiff(moment(values.endTimestamp).diff(moment(start), 'd'));
            }
        } else if (filterName === 'endTimestamp') {
            if (value) {
                let tempEnd = value.local();
                if (moment(tempEnd).isAfter(moment().local())) {
                    tempEnd = moment().local();
                }
                form.setFieldValue('endTimestamp', tempEnd, false);
                setEndTimestamp(tempEnd);
                if (moment(tempEnd).local().diff(values.startTimestamp, 'm') >= maxRange) {
                    const start = moment(tempEnd).subtract(maxRange, 'm').local();
                    form.setFieldValue('startTimestamp', start, false);
                    setStartTimestamp(start);
                }
                else if (moment(tempEnd).isSameOrBefore(moment(values.startTimestamp))) {
                    const start = moment(tempEnd).subtract(1, 'd').local();
                    form.setFieldValue('startTimestamp', start, false);
                    setStartTimestamp(start);
                }
                setSelectedDateDiff(moment(values.endTimestamp).diff(moment(values.startTimestamp),'d'));
            }
        }
    };

    return (
        <div>
            <React.Fragment>
                <div className={classes.bobTimestampFilter}>
                    <div className={classes.startTime}>
                        <div className={clsx(classes.labelContainer, className)}><label>{'From '}</label></div>
                        <DateField
                            dateFormat={dateFormat}
                            isClearable={false}
                            margin="dense"
                            maxDate={new Date()}
                            minDate={new Date(2015, 0, 1)}
                            name="startTimestamp"
                            onChange={handleChange('startTimestamp')}
                            showTimeSelect={showTimeSelect}
                            timeFormat={timeFormat}
                            timeIntervals={interval < 5 ? 5 : interval}
                            value={values.startTimestamp.toDate()}
                            disabled={true}
                            disableUnderline={true}
                            InputProps={{
                                popperoverride: classes.dateClassName
                            }}
                            open={true}
                        />
                    </div>
                    <div className={classes.endTime}>
                        <div className={clsx(classes.labelContainer, className)}><label>{'To '}</label></div>
                        <DateField
                            dateFormat={dateFormat}
                            isClearable={false}
                            margin="dense"
                            maxDate={new Date()}
                            minDate={new Date(2015, 0, 1)}
                            name="endTimestamp"
                            onChange={handleChange('endTimestamp')}
                            showTimeSelect={showTimeSelect}
                            timeFormat={timeFormat}
                            timeIntervals={interval < 5 ? 5 : interval}
                            touched={touched.endTimestamp}
                            value={values.endTimestamp.toDate()}
                            disabled={true}
                            disableUnderline={true}
                            InputProps={{
                                popperoverride: classes.dateClassName
                            }}
                            open={true}
                        />
                    </div>
                </div>
            </React.Fragment>
            <div className={classes.footerWithoutScroll}>
                <p className={classes.infoText}>{maxMessage}</p>
            </div>
        </div>
    );
};

ExportDataTimestamp.defaultProps = {
    dateFormat:              'yyyy-MM-dd h:mm a',
    showTimeSelect:          true,
    timeFormat:              'h:mm a',
    timeIntervals:           15,
    disableIntervalSelector: false
};

export default ExportDataTimestamp;
